import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import {
   getCurrentUserIsSystemUser,
   getCurrentUserIsTrueSystemUser,
} from 'src/app/authentication/state/authentication.selector';
import { CallDetailDialogComponent } from 'src/app/common-components/call-details-dialog/call-detail-dialog.component';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { getCallExtension } from 'src/app/common-components/helpers/call-logs-helper';
import { getHangUpCallsPrivileges } from 'src/app/privileges/privileges.selector';
import {
   systemCallDetailsAdded,
   systemCallDetailsUpdated,
} from 'src/app/signalr/call-details-hub/call-details-hub.actions';
import { SystemCallDetailsHubService } from 'src/app/signalr/call-details-hub/system-call-details-hub.service';
import { activeCallsChanged } from 'src/app/signalr/current-viewing-active-calls-hub/currently-viewing-active-calls-hub.actions';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ActiveCallHangupConfirmationDialogComponent } from '../../active-call-hangup-confirmation-dialog/active-call-hangup-confirmation-dialog.component';
import { buildHangupConfirmationMessage } from '../../helpers/active-call-hangup-confirmation-message.helper';
import {
   ActiveCallsRegisterListItem,
   ActiveCallsRegisterResults,
} from '../../models/active-calls-register-results.model';
import { GetActiveCalls, GetActiveCalls_activeCallsConnection_items } from '../../queries/generated/GetActiveCalls';
import { GetActiveCallsReadOnly } from '../../queries/generated/GetActiveCallsReadOnly';
import { ActiveCallsRegisterApiService } from '../../services/active-calls-register-api.service';
import {
   ActiveCallRegisterType,
   ActiveCallsRegisterSessionStorageService,
} from '../../services/active-calls-register-session-storage.service';
import * as SystemActiveCallsDetailActions from './system-active-calls.actions';
import { systemActiveCallsRegisterAdapterActions } from './system-active-calls.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './system-active-calls.reducer';
import { selectFilters } from './system-active-calls.selectors';

@Injectable()
export class SystemActiveCallsRegisterEffects {
   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private activeCallsRegisterApiService: ActiveCallsRegisterApiService,
      private dialog: MatDialog,
      private dialogService: LcsDialogService,
      private sessionStorageService: ActiveCallsRegisterSessionStorageService,
      private callDetailsHub: SystemCallDetailsHubService
   ) {}

   loadActiveCallsList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(systemActiveCallsRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectCurrentRoute)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([_, filters, route, orderingOptions, quickFilters]) => {
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            const sessionOrderingOptions = this.sessionStorageService.getOrderingOptions(
               ActiveCallRegisterType.SystemActive
            );

            const ordering = sessionOrderingOptions ?? orderingOptions;
            if (sessionOrderingOptions) {
               this.store.dispatch(
                  SystemActiveCallsDetailActions.setOrderingOptionsFromSessionStorage({ orderingOptions: ordering })
               );
            }

            return this.activeCallsRegisterApiService
               .getActiveCalls(null, filters, quickFilters, ordering, currentRouteUrl)
               .pipe(
                  map((result) =>
                     systemActiveCallsRegisterAdapterActions.loadSuccess(
                        this.convertGetActiveCallsResultToLoadSuccessProps(
                           result.data as GetActiveCallsReadOnly,
                           currentRouteUrl
                        )
                     )
                  ),
                  catchError((error) => of(systemActiveCallsRegisterAdapterActions.loadFailure(error)))
               );
         })
      )
   );

   loadActiveCallsPage$ = createEffect(() =>
      this.actions$.pipe(
         ofType(systemActiveCallsRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([_, endCursor, quickFilters, filters, orderingOptions, route]) => {
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.activeCallsRegisterApiService
               .getActiveCalls(endCursor, filters, quickFilters, orderingOptions, currentRouteUrl)
               .pipe(
                  map((result) => {
                     const sessionOrderingOptions = this.sessionStorageService.getOrderingOptions(
                        ActiveCallRegisterType.SystemActive
                     );
                     if (sessionOrderingOptions) {
                        orderingOptions = sessionOrderingOptions;
                        this.store.dispatch(
                           SystemActiveCallsDetailActions.setOrderingOptionsFromSessionStorage({ orderingOptions })
                        );
                     }
                     return systemActiveCallsRegisterAdapterActions.loadPageSuccess(
                        this.convertGetActiveCallsResultToLoadSuccessProps(
                           result.data as GetActiveCallsReadOnly,
                           currentRouteUrl
                        )
                     );
                  }),
                  catchError((error) => of(systemActiveCallsRegisterAdapterActions.loadPageFailure(error)))
               );
         })
      )
   );

   orderingOptionsChanged$ = createEffect(() =>
      this.actions$.pipe(
         ofType(systemActiveCallsRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([_, quickfilters, orderingOptions, filters, route]) => {
            this.sessionStorageService.setOrderingOptions(ActiveCallRegisterType.SystemActive, orderingOptions);
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.loadNewData(quickfilters, filters, orderingOptions, currentRouteUrl);
         })
      )
   );

   toggleAutoRefresh$ = createEffect(() =>
      this.actions$.pipe(
         ofType(SystemActiveCallsDetailActions.toggleAutoRefresh),
         debounceTime(300),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions, route]) => {
            if (!action.isAutoRefresh) return EMPTY;
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.loadNewData(quickFilters, filters, orderingOptions, currentRouteUrl);
         })
      )
   );

   quickFilterChanged$ = createEffect(() =>
      this.actions$.pipe(
         ofType(systemActiveCallsRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([_, quickFilters, filters, orderingOptions, route]) => {
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.loadNewData(quickFilters, filters, orderingOptions, currentRouteUrl);
         })
      )
   );

   activeCallNotification$ = createEffect(() =>
      this.actions$.pipe(
         ofType(activeCallsChanged, SystemActiveCallsDetailActions.hangupActiveCallSuccess),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([_, quickFilters, filters, orderingOptions, route]) => {
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.loadNewData(quickFilters, filters, orderingOptions, currentRouteUrl);
         })
      )
   );

   registerAsViewingActiveCalls$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(systemActiveCallsRegisterAdapterActions.pageEnter),
            withLatestFrom(
               this.store.pipe(select(getSelectedAccountId)),
               this.store.pipe(select(getCurrentUserIsTrueSystemUser)),
               this.store.pipe(select(selectCurrentRoute))
            ),
            tap(([_, accountId, isSystemUser, route]) => {
               const isSystemRoute = route.url[0].path === 'activecalls';
               const targetAccountId = isSystemRoute ? null : accountId;
               this.callDetailsHub.registerCallDetailEvents(targetAccountId, isSystemUser);
            })
         ),
      { dispatch: false }
   );

   openDialog$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(SystemActiveCallsDetailActions.openDialog),
            withLatestFrom(
               this.store.pipe(select(getCurrentUserIsSystemUser)),
               this.store.pipe(select(getHangUpCallsPrivileges))
            ),
            switchMap(([action, isSystemUser, canHangupCalls]) =>
               this.activeCallsRegisterApiService.getActiveCallDetailDialogData(action.callLogs.callDetailId).pipe(
                  tap((callData) => {
                     this.dialog.open(CallDetailDialogComponent, {
                        data: {
                           callLogs: action.callLogs,
                           callData: callData.data.activeCall,
                           relatedCallDetails: (callData as any).relatedCallDetails,
                           isSystemUser,
                           canHangupCalls: canHangupCalls,
                           registerType: ActiveCallRegisterType.SystemActive,
                        },
                        width: '800px',
                        autoFocus: false,
                     });
                  })
               )
            )
         ),
      { dispatch: false }
   );

   hangupActiveCall$ = createEffect(() =>
      this.actions$.pipe(
         ofType(SystemActiveCallsDetailActions.hangupActiveCall),
         switchMap((action) => {
            const message = buildHangupConfirmationMessage(action.activeCall);

            const dialogRef = this.dialogService.open(ActiveCallHangupConfirmationDialogComponent, {
               data: { message, activeCall: action.activeCall },
               panelClass: [DialogSize.Medium, 'dialog-container'],
               disableClose: true,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (!confirmed) return EMPTY;
                  return this.activeCallsRegisterApiService
                     .hangupActiveCall(
                        action.activeCall.serverName?.split(',')[0] ?? null,
                        action.activeCall.callerChannelId,
                        action.activeCall.callDetailId
                     )
                     .pipe(map(() => SystemActiveCallsDetailActions.hangupActiveCallSuccess()));
               })
            );
         })
      )
   );

   callDetailUpdated$ = createEffect(
      () => this.actions$.pipe(ofType(systemCallDetailsAdded, systemCallDetailsUpdated)),
      {
         dispatch: false,
      }
   );

   private loadNewData(quickFilters: any, filters: any, orderingOptions: any, currentRouteUrl: string) {
      return this.activeCallsRegisterApiService
         .getActiveCalls(null, filters, quickFilters, orderingOptions, currentRouteUrl)
         .pipe(
            map((result) =>
               systemActiveCallsRegisterAdapterActions.loadSuccess(
                  this.convertGetActiveCallsResultToLoadSuccessProps(
                     result.data as GetActiveCallsReadOnly,
                     currentRouteUrl
                  )
               )
            ),
            catchError((error) => of(systemActiveCallsRegisterAdapterActions.loadFailure(error)))
         );
   }

   private convertGetActiveCallsResultToLoadSuccessProps(
      getActiveCalls: GetActiveCalls | GetActiveCallsReadOnly,
      currentRouteUrl: string
   ): ActiveCallsRegisterResults {
      const isReadOnly = 'activeCallReadOnliesConnection' in getActiveCalls;
      const connection = isReadOnly
         ? (getActiveCalls as GetActiveCallsReadOnly).activeCallReadOnliesConnection
         : (getActiveCalls as GetActiveCalls).activeCallsConnection;

      return {
         endCursor: connection.pageInfo.endCursor,
         hasNextPage: connection.pageInfo.hasNextPage,
         items: connection.items.map(this.convertActiveCallsRegisterListItem),
         totalCount: connection.totalCount,
      };
   }

   private convertActiveCallsRegisterListItem(
      item: GetActiveCalls_activeCallsConnection_items
   ): ActiveCallsRegisterListItem {
      return {
         accountName: item.account?.name ?? null,
         callDestination: getCallExtension(
            { ...item.destinationExtension },
            item.callDirectionTypeId,
            item.destinationNumber,
            true
         ),
         callDetailId: item.callDetailId,
         callDirection: item.callDirectionTypeId,
         callDispositionTypeId: item.callDispositionTypeId,
         callRecordingId: item.callRecording?.callRecordingId ?? null,
         callSource: getCallExtension({ ...item.sourceExtension }, item.callDirectionTypeId, item.sourceNumber),
         destinationExtension: item.destinationExtension,
         destinationNumber: item.destinationNumber,
         originalDestinationNumber: item.originalDestinationNumber,
         phoneNumber: item.phoneNumber?.phoneNumber ?? null,
         callRate: item.callRate ?? 0,
         startTime: item.startTime,
         callerChannelId: item.callerChannelId,
         serverName: item.serverName,
         sipCallId: item.sipCallId,
         providerId: item.providerId,
         sourceProviderId: item.sourceProviderId,
         destinationProviderId: item.destinationProviderId,
         callRecordingFile: item.callRecording?.file ?? null,
         isPhoneCallThatWentDirectlyToSourceExtension: null,
      };
   }

}
